package com.eladfinish.bible.contest.constants

import com.eladfinish.bible.contest.appcontent.HebrewMarks
import com.eladfinish.bible.contest.appcontent.SearchSymbols
import com.eladfinish.bible.contest.constants.StringConstants.Regex.GroupName.book
import com.eladfinish.bible.contest.constants.StringConstants.Regex.GroupName.chapter
import com.eladfinish.bible.contest.constants.StringConstants.Regex.GroupName.content
import com.eladfinish.bible.contest.constants.StringConstants.Regex.GroupName.contentBeginning
import com.eladfinish.bible.contest.constants.StringConstants.Regex.GroupName.verse
import com.eladfinish.bible.contest.extensions.formatString
import com.eladfinish.bible.contest.platform.getLineSeparator

/**
 * Created by Elad on 10/11/20.
 */

object StringConstants {

    val NEW_LINE: String = getLineSeparator()

    const val ALL_CHAPTERS = "allChapters"

    const val BLANK_LABEL = "____" // "_".repeat(4)

    const val COLON_SPLITTER = "׃" // colonSplitter - SOF PASUK. in case of dot replace with \\.
    const val SHORESH = "√"
    const val DASH = " - "
    const val MAKAF = '־'
    const val DAGESH = 'ּ'
    const val RAFE = 'ֿ'
    const val PASEQ = '׀'
    const val ZAQEF_QATON = '֔'
    const val SEGOLTA = '֒'
    const val NUN_MENUZERET = '׆'
    const val SPACE_MAKAF = "[\\s$MAKAF]"
    const val HTML_LINE_BREAK = "<br>"
    const val DATE_SYMBOL = SearchSymbols.DATE
    const val HASH_TAG_SYMBOL = SearchSymbols.NUMBER

    const val MYSTERY_CHAR_ZWNBS = 65279 // Zero-width non-breaking space

    // don't consider paseq as a word in itself
    const val REGEX_SPACE = "\\s(?!׀)" //TODO: don't forget to add toRegex() if it used as regex in Kotlin

    const val CHAPTER_NUMBER_REGEX = "(?<=,)(\\d+?)[,\\]]" //look behind

    const val FIVE_SPACES = "     "

    const val NO_BREAK_SPACE = "\u00A0" // NBSP

    // replace " +" and not "\\s+"
    //NO_BREAK_SPACE
    const val SPACE_OR_NO_BREAK_SPACE = "[ $NO_BREAK_SPACE]"

    const val MYSTERY_CHAR_ZWJ = 8205.toChar() // zero width joiner

    const val SIFREY_EMET = "תהילים משלי איוב"

    object Regex {
        const val ALEF_TAV_ = "א-ת"
        private const val ALEF_TAV_SPACE = "[א-ת\\s]"

        //    String ot = "[" + "\\." + aleph_tav + "]"; // ot is an Hebrew letter or a dot
        const val OT = "[.$ALEF_TAV_]" // ot is a Hebrew letter or a dot
        const val ALEF_TAV = "[א-ת]"
        const val OTIOT_1_TO_3 = "($ALEF_TAV{1,3})"
        const val OTIOT_2_PLUS = "$ALEF_TAV{2,}"
        const val TEAMIM_CHARS = "[" + "ֽ֪֑֧֖֪֥֭֚֮֨֜֝֕֔ׄ֒֫֬֡׀֣֤֛֦ׅ֘֙֟֗֓֞֠֩׆" + "]"
        const val NIKUD_CHARS = HebrewMarks.NIKUD_CHARS

        const val OTIOT = "($ALEF_TAV{1,3})"

        object GroupName {
            const val book = "book"
            const val chapter = "chapter"
            const val contentBeginning = "contentBeginning"
            const val verse = "verse"
            const val content = "content"

            const val otPasuk = "otPasuk"
            const val verseContent = "verseContent"
            const val curlyBrackets = "curlyBrackets"
        }

        const val FIRST_VERSE =  // TODO: 1/1/22 give name to each capturing group
            "(?<$book>$ALEF_TAV_SPACE+?)\\sפרק\\s(?<$chapter>$ALEF_TAV{1,3})\\s+(?:\\{.+?\\}\\s)?" +
                    "(?<$contentBeginning>(?<$verse>$ALEF_TAV{1,3})\\s(?<$content>.*))"

        const val NOT_SPACE_MAKAF = "[^\\s$MAKAF]"
        const val NOT_ALEF_TAV_SPACE = "[^א-ת$MAKAF$COLON_SPLITTER\\s]"

        // A "word character" as Android's ICU regex defines it for \b, restricted to what this
        // app's text contains: ASCII word chars, Hebrew letters, and Hebrew nikud/te'amim
        // (combining marks — maqaf ־, paseq ׀ and sof pasuq ׃ are punctuation, so excluded).
        // Use it in a one-sided lookaround to replace a \b that sits next to a Hebrew letter:
        // `\bא` ≡ `(?<!$WORD_CHAR)א` and `א\b` ≡ `א(?!$WORD_CHAR)`. JS RegExp's \b is
        // ASCII-only and never fires there, so a literal \b silently fails to match on web.
        const val WORD_CHAR = "[0-9A-Za-z_א-ת\\u0591-\\u05BD\\u05BF\\u05C1\\u05C2\\u05C4\\u05C5\\u05C7]"

        // A full two-sided \b built on WORD_CHAR, for the rare spot where the character next to
        // the \b may or may not be a word character. Prefer the one-sided WORD_CHAR lookaround
        // whenever the adjacent character is known to be a Hebrew letter.
        // `\s` plus NBSP, for use inside a character class. Android (ICU) and JS match NBSP with
        // `\s`, but Kotlin/Native (iOS) and the desktop JVM don't, and the Bible text is full of
        // NBSPs. Written as the `\u00A0` escape, not a literal NBSP, so code that rewrites
        // whitespace in a pattern string (e.g. GetParashaNameSpannedUseCase) leaves it alone.
        const val WHITESPACE = "\\s\\u00A0"

        const val WORD_CHAR_BOUNDARY = "(?:(?<=$WORD_CHAR)(?!$WORD_CHAR)|(?<!$WORD_CHAR)(?=$WORD_CHAR))"

        // The search screen's "!" sign. Native \b relies on each
        // platform's own definition of "word character" (JS RegExp's \w is ASCII-only, so \b
        // never fires around Hebrew letters there), hence the emulation. It must be built on
        // WORD_CHAR, not on "anything but space/maqaf": sof pasuq ׃ and paseq ׀ are punctuation,
        // and treating them as word characters meant no boundary was found between the last
        // word of a verse and its ׃ — so "!:" at a verse's end never hit.
        const val WORD_BOUNDARY = WORD_CHAR_BOUNDARY

        const val PEREK = "${WORD_BOUNDARY}פרק\\s${ALEF_TAV}{1,3}$WORD_BOUNDARY"

        const val VERSE = ("(?<${GroupName.curlyBrackets}>(?:\\{.+?\\}\\s)?)" //1.(curlyBrackets)
                + "(?<${GroupName.otPasuk}>" + ALEF_TAV + "{1,3})\\s" //2.(otPasuk)
                + "(?<${GroupName.verseContent}>.*)") //3.(verseContent)

        // wrap starting colonSplitter with positive look-behind, such that the colonSplitter would not be consumed.
        const val PASUK_BEGINNING = "(?<=${COLON_SPLITTER})\\s(?:\\{.+?\\}\\s)?${ALEF_TAV}{1,3}\\s"
        const val OTIOT_ALIYAH = "\\[.+?\\]"

        //getAliyotRegex
        fun getAliyahRegex(str: String): String = "\\s?\\{%s\\}\\s?\\s?".formatString(str)

    }

}
